import React from 'react';
import { type Tone, toneClasses } from '../lib/tones';
import { Icon } from './Icon';
import type { IconName } from './Icon';

/**
 * The one status pill for both apps.
 *
 * Badge owns GEOMETRY; the colour comes from a semantic `tone`. Domains whose
 * scale is not semantic — the 5-step order-status ramp — compose
 * `badgeBaseClass` with their own colour table instead of polluting the tone
 * union with values the other app would never use. That is the same
 * class-bundle composition already used by `modalClasses` / `tableClasses`.
 *
 * Callers that map a domain state to a tone should do it in a small local
 * component (`StatusBadge` in platform-admin, `OrderStatusBadge` in tenant)
 * rather than at every call site.
 *
 * @example
 * <Badge tone="success">Activo</Badge>
 * <Badge tone="danger" icon="block">Bloqueado</Badge>
 */

/**
 * Geometry only — no colour. Exported so a domain badge with a non-semantic
 * scale reuses the exact same pill.
 *
 * `shrink-0` and `whitespace-nowrap` are load-bearing, not cosmetic: without
 * them a badge is a freely shrinkable flex child, and it is what let the
 * dashboard squeeze the order price out of its card. `max-w-full truncate`
 * is the counterpart — long computed labels (the admin renders
 * "En prueba · hasta 31/12/2026") ellipsize inside their cell instead of
 * overflowing it. Pair with a `title` when the label can be truncated.
 *
 * ⚠️ `truncate` on the flex box itself never draws the «…»: text-overflow
 * does not apply to an inline-flex container, and with `justify-center` a
 * long label overflowed on BOTH sides and was clipped («AS SIN VINCULAR · 99
 * CON», 2026-10-24). The label goes in its own `min-w-0 truncate` span.
 */
export const badgeBaseClass =
    'inline-flex items-center justify-center shrink-0 whitespace-nowrap max-w-full truncate rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border ring-1 ring-black/5 dark:ring-white/10';

export interface BadgeProps {
    children: React.ReactNode;
    /** Semantic colour. Defaults to `neutral`. */
    tone?: Tone;
    /** Optional leading Material Symbols ligature. */
    icon?: IconName;
    className?: string;
    /** Native tooltip — pass the full text whenever the label can truncate. */
    title?: string;
}

const Badge: React.FC<BadgeProps> = ({ children, tone = 'neutral', icon, className = '', title }) => {
    const t = toneClasses[tone] ?? toneClasses.neutral;
    return (
        <span
            title={title}
            className={`${badgeBaseClass} ${t.pillBg} ${t.pillText} ${t.pillBorder} ${className}`}
        >
            {icon ? <Icon name={icon} className="!text-[12px] mr-1 shrink-0" aria-hidden="true" /> : null}
            <span className="min-w-0 truncate">{children}</span>
        </span>
    );
};

export default Badge;
